import React, { useEffect, useState } from 'react';

import type { Image } from '../../services/api';
import api from '../../services/api';

import styles from './SiteSettingsEditor.module.css';

/**
 * SiteSettingsEditor Component
 *
 * Admin interface for managing site-wide settings:
 * - Artist photo (circular menu center) with image picker
 * - Artist name
 * - Site title
 */
const SiteSettingsEditor: React.FC = () => {
  const [artistPhotoUrl, setArtistPhotoUrl] = useState('');
  const [artistName, setArtistName] = useState('');
  const [siteTitle, setSiteTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    readonly type: 'success' | 'error';
    readonly text: string;
  } | null>(null);

  // Image picker state
  const [images, setImages] = useState<ReadonlyArray<Image>>([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Load current settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.siteSettings.get();
        setArtistPhotoUrl(settings.artistPhotoUrl ?? '');
        setArtistName(settings.artistName ?? '');
        setSiteTitle(settings.siteTitle ?? '');
      } catch (error) {
        console.error('Failed to load site settings:', error);
        setMessage({ type: 'error', text: 'Failed to load settings' });
      }
    };

    loadSettings();
  }, []);

  // Load all images for the picker
  useEffect(() => {
    const loadImages = async () => {
      try {
        const allImages = await api.images.getAll();
        setImages(allImages);
      } catch (error) {
        console.error('Failed to load images:', error);
      } finally {
        setLoadingImages(false);
      }
    };

    loadImages();
  }, []);

  /**
   * Handle image selection from the picker
   * @param image
   */
  const handleSelectImage = (image: Image): void => {
    // Use the medium URL for artist photo (good balance of quality and size)
    setArtistPhotoUrl(image.mediumUrl || image.url);
    setShowImagePicker(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await api.siteSettings.update({
        artistPhotoUrl,
        artistName,
        siteTitle,
      });

      setMessage({ type: 'success', text: 'Settings updated successfully!' });
    } catch (error) {
      console.error('Failed to update settings:', error);
      setMessage({ type: 'error', text: 'Failed to update settings' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Site Settings</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Artist Photo Selector */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Artist Photo</label>

          {/* Current selection preview */}
          <div className={styles.photoSelector}>
            {artistPhotoUrl ? (
              <div className={styles.currentPhoto}>
                <img
                  src={artistPhotoUrl}
                  alt="Current Artist Photo"
                  className={styles.previewImage}
                />
                <button
                  type="button"
                  onClick={() => setShowImagePicker(true)}
                  className={styles.changePhotoButton}
                >
                  Change Photo
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowImagePicker(true)}
                className={styles.selectPhotoButton}
              >
                Select Photo
              </button>
            )}
          </div>

          <p className={styles.hint}>Select an image from your uploaded images</p>

          {/* Image Picker Modal */}
          {showImagePicker ? (
            <div className={styles.imagePickerOverlay}>
              <div className={styles.imagePickerModal}>
                <div className={styles.imagePickerHeader}>
                  <h3>Select Artist Photo</h3>
                  <button
                    type="button"
                    onClick={() => setShowImagePicker(false)}
                    className={styles.closeButton}
                  >
                    &times;
                  </button>
                </div>

                <div className={styles.imagePickerContent}>
                  {loadingImages ? (
                    <p className={styles.loadingText}>Loading images...</p>
                  ) : images.length === 0 ? (
                    <p className={styles.emptyText}>
                      No images found. Upload some images in the Image Manager first.
                    </p>
                  ) : (
                    <div className={styles.imageGrid}>
                      {images.map((image) => (
                        <button
                          key={image._id}
                          type="button"
                          className={`${styles.imageOption} ${artistPhotoUrl === (image.mediumUrl || image.url) ? styles.selected : ''}`}
                          onClick={() => handleSelectImage(image)}
                        >
                          <img
                            src={image.thumbnailUrl ?? image.url}
                            alt={image.alt ?? image.originalName}
                          />
                          <span className={styles.imageName}>
                            {image.title ?? image.originalName}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Artist Name */}
        <div className={styles.formGroup}>
          <label htmlFor="artistName" className={styles.label}>
            Artist Name
          </label>
          <input
            type="text"
            id="artistName"
            value={artistName}
            onChange={(e) => setArtistName(e.target.value)}
            placeholder="Milen Dimitrov"
            className={styles.input}
          />
        </div>

        {/* Site Title */}
        <div className={styles.formGroup}>
          <label htmlFor="siteTitle" className={styles.label}>
            Site Title
          </label>
          <input
            type="text"
            id="siteTitle"
            value={siteTitle}
            onChange={(e) => setSiteTitle(e.target.value)}
            placeholder="Portfolio"
            className={styles.input}
          />
        </div>

        {/* Submit Button */}
        <button type="submit" disabled={loading} className={styles.submitButton}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>

        {/* Message */}
        {message ? (
          <div className={`${styles.message} ${styles[message.type]}`}>{message.text}</div>
        ) : null}
      </form>
    </div>
  );
};

export default SiteSettingsEditor;
